import { useCallback, useState } from 'react'
import type { ColumnDef } from '@axilon-apps/libs-ui-components'

import { useGetUsersQuery } from '../../../../state'
import type { Model } from '../../../../types'
import UsersTable from './components/UsersTable/UsersTable'
import type { ExtraRowAction } from './components/UsersTable/users-table-columns'
import UsersPageHeader from './components/UserPageHeader/UsersPageHeader'
import UsersFilter from './components/UsersFilter/UsersFilter'
import type { UsersFiltersFormValues } from './types/users-filters-form'

interface Props {
  extraActions?: ExtraRowAction[]
  /** Optional host-app columns inserted after the Role column. */
  extraColumns?: ColumnDef<Model.User>[]
  /** Show the status filter inline instead of behind a filter toggle. */
  inlineFilters?: boolean
  /** Show email fields and the email table column. */
  showEmail?: boolean
}

const UsersPage = ({
  extraActions,
  extraColumns,
  inlineFilters = false,
  showEmail = true
}: Props = {}) => {
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(5)
  const [filters, setFilters] = useState<UsersFiltersFormValues>({
    search: '',
    enabled: undefined,
    showServiceAccounts: false
  })
  const search = filters.search?.trim() || undefined

  const { data, isLoading, isFetching } = useGetUsersQuery(
    {
      page,
      perPage,
      enabled: filters.enabled,
      search,
      serviceAccounts: filters.showServiceAccounts ? 'include' : 'exclude'
    },
    { refetchOnMountOrArgChange: true }
  )

  const paginate = (newPage: number, newPerPage: number) => {
    setPage(newPage)
    setPerPage(newPerPage)
  }

  const onFiltersChange = useCallback((newFilters: UsersFiltersFormValues) => {
    setFilters(newFilters)
    setPage(1)
  }, [])

  return (
    <div className="p-[var(--Content-padding)]">
      <div className="flex flex-col gap-6">
        <UsersPageHeader showEmail={showEmail} />
        <UsersFilter filters={filters} onChange={onFiltersChange} inline={inlineFilters} />
        <UsersTable
          users={data?.items || []}
          async={{ isLoading, isFetching }}
          pagination={{ paginate, page, perPage, total: data?.total || 0 }}
          extraActions={extraActions}
          extraColumns={extraColumns}
          showEmail={showEmail}
        />
      </div>
    </div>
  )
}

export default UsersPage
